"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import AvisImpotMaquette from "./TaxNoticeMockup";
import {
    Avancer,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    dateLisible,
    euros,
    eurosPrecis,
    remplir,
    useParcours,
} from "./Journey";
import {
    eligible,
    joursAvantEcheance,
    parts,
    ANNEE_OPTION_PAR_DEFAUT,
    RFR_PLAFONDS,
} from "@/lib/tools/incomeTax";
import { reprendre } from "@/lib/tools/dossier";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";

/**
 * Ai-je droit au versement libératoire ?
 *
 * Volontairement séparé du comparateur : ce sont deux intentions différentes.
 * Ici on répond à « ai-je le droit », là-bas à « est-ce que j'y gagne ». Le
 * parcours convient au premier, qui se termine par oui ou non ; il étoufferait
 * le second, qu'on manipule pour explorer.
 *
 * UNE CONDITION DE REVENU, ET UN DOUBLE DÉCALAGE. Le droit ne regarde ni
 * l'activité ni l'ancienneté, et le chiffre d'affaires ne compte que pour
 * rester dans le régime micro : ce qui décide, c'est le revenu fiscal de
 * référence du foyer, multiplié par ses parts. Mais lequel ? L'article 151-0
 * du CGI lit celui de l'avant-dernière année, sur l'avis reçu l'année
 * précédente. Chaque carte du premier écran le dit, parce que c'est là que
 * tout le monde se trompe.
 *
 * LA SITUATION DÉCIDE DE L'ANNÉE ET DE L'ÉCHÉANCE. Un lecteur qui découvre le
 * sujet ne sait pas « pour quelle année il veut l'option » ; il sait s'il est
 * déjà en activité ou s'il crée. C'est cette situation qu'on lui demande : elle
 * fixe l'année à vérifier (toujours tirée de RFR_PLAFONDS) et le bon message
 * d'échéance, 30 septembre pour une activité en cours, fin du troisième mois
 * suivant la création pour un créateur.
 *
 * LE NOMBRE DE PARTS EST D'ABORD DEVINÉ, PUIS CORRIGEABLE. Le parcours le
 * reconstitue depuis la situation familiale, qui se répond sans réfléchir, et
 * le résultat laisse reporter le chiffre exact de l'avis : un parent isolé, un
 * veuf avec enfants ou une garde alternée produisent des parts qu'aucune
 * question simple ne retrouve. Les parts qui comptent sont celles de l'avis
 * dont on recopie le montant, et l'aide de chaque question le rappelle.
 *
 * L'ÉCHÉANCE N'EST PAS UN REFUS. La fonction `eligible` n'évalue que le
 * plafond ; la date limite est affichée à part, dans le ton qui convient à la
 * situation.
 */

/* Le parcours, écran par écran, en phases nommées. */
const PHASES = { situation: 0, couple: 1, enfants: 1, caseT: 1, rfr: 2, resultat: 2 };

/* Les trois situations du premier écran, dans l'ordre des cartes. */
const SITUATIONS = ["actif", "creeCetteAnnee", "creeAnProchain"];

/* Le revenu affiché sur la maquette d'avis : un exemple, jamais celui du lecteur. */
const MONTANT_EXEMPLE = 31870;

const L = {
    fr: {
        phases: ["Votre situation", "Votre foyer", "Votre avis d'impôt"],
        retour: "Retour",
        recommencer: "Recommencer",
        verifier: "Voir si j'y ai droit",
        corriger: "Corriger le montant saisi",

        qSituation: "Où en êtes-vous ?",
        qSituationAide:
            "Votre situation fixe l'année à vérifier et la date limite pour opter. L'administration ne regarde pas vos revenus de l'année en cours, mais ceux de deux ans avant l'année d'option : chaque carte vous dit lesquels.",
        actif: "Je suis déjà micro-entrepreneur",
        creeCetteAnnee: "Je crée mon activité d'ici la fin de {annee}",
        creeAnProchain: "Je crée mon activité en {annee}",
        anneeDetail:
            "Option pour {annee} : revenu fiscal de référence de {rfr}, sur l'avis reçu en {avis}",
        creationDetail: "Option prise à la création, ou dans les trois mois qui suivent",
        anneeRestant: "{n} jours pour opter, jusqu'au {date}",
        anneeDernierJour: "Dernier jour pour opter, le {date}",
        anneePassee: "Date limite du {date} passée",

        qCouple: "Vivez-vous seul ou en couple ?",
        qCoupleAide:
            "Le plafond est fixé pour une part, et votre foyer en compte une ou plusieurs : une personne seule en a une, un couple marié ou pacsé, qui déclare ensemble, en a deux. Son plafond passe donc de {seul} à {couple}. Répondez pour {rfr}, l'année de l'avis qui compte : si vous vous êtes marié, pacsé ou séparé depuis, c'est la situation de cet avis qui vaut.",
        seul: "Seul",
        seulDetail:
            "Célibataire, divorcé, séparé ou veuf. Veuf avec enfants à charge : reportez ensuite les parts de votre avis.",
        couple: "En couple",
        coupleDetail:
            "Marié ou pacsé, une seule déclaration pour deux. Un concubin déclare seul.",

        qEnfants: "Combien d'enfants à charge ?",
        qEnfantsAide:
            "Chaque enfant ajoute une demi-part, une part entière à partir du troisième, et le plafond suit : avec un enfant, le vôtre passe à {plafond1}. Comptez ceux qui figuraient à votre charge sur l'avis reçu en {avis} ; le nombre exact de parts pourra être ajusté sur le résultat.",
        enfants0: "Aucun",
        enfants1: "Un",
        enfants2: "Deux",
        enfants3: "Trois ou plus",
        enfants3Detail: "Ajustable ensuite si vous en avez plus",

        qRfr: "Quel est votre revenu fiscal de référence de {rfr} ?",
        qRfrAide:
            "Pour une option en {annee}, seuls vos revenus de {rfr} comptent. Ils figurent sur l'avis arrivé en {avis}.",
        partsMaquette:
            "Le nombre de parts figure aussi sur votre avis : s'il diffère de {parts}, vous pourrez le corriger sur le résultat.",
        reporter: "Et le vôtre ?",
        champRfr: "Revenu fiscal de référence de {rfr}",
        champRfrAide:
            "En euros, tel qu'il figure sur l'avis, sans centimes. Le montant exact compte : quelques euros peuvent faire basculer le résultat.",

        resultatPour: "Votre droit pour {annee}",
        oui: "Vous y avez droit",
        non: "Vous n'y avez pas droit pour {annee}",
        plafondParPart: "Plafond par part",
        vosParts: "Vos parts",
        votrePlafond: "Votre plafond",
        partSingulier: "part",
        partPluriel: "parts",
        votreRfr: "Votre revenu fiscal de référence de {rfr}",
        margeLibelle: "Marge sous le plafond",
        depassementLibelle: "Dépassement du plafond",
        ouiMotif:
            "Votre revenu fiscal de référence de {rfr}, {montant}, ne dépasse pas le plafond de votre foyer, {plafond}.",
        ouiMarge:
            "Il vous reste {marge} de marge. Une seule autre condition s'ajoute : rester sous les plafonds de chiffre d'affaires de la micro-entreprise, ce qui est votre cas tant que vous êtes micro-entrepreneur.",
        nonMotif:
            "Votre revenu fiscal de référence de {rfr}, {montant}, dépasse le plafond de votre foyer, {plafond}, de {depassement}.",
        nonRegle:
            "Cette condition de revenu vient de l'article 151-0 du code général des impôts : le revenu fiscal de référence de l'avant-dernière année ne doit pas dépasser la limite de la deuxième tranche du barème, {parPart} par part. Votre métier et la date de votre création n'y changent rien. Votre chiffre d'affaires non plus, tant que vous restez dans le régime micro.",
        nonSuite:
            "L'année de revenus regardée avance d'un an à chaque fois : si vos revenus baissent, vous pouvez repasser sous le plafond l'an prochain. Pour un foyer imposé comme le vôtre, l'option serait justement intéressante. En attendant, vos revenus micro sont imposés au barème avec le reste du foyer, après l'abattement forfaitaire de votre activité.",

        ajusterLibelle: "Parts sur votre avis",
        ajusterAide:
            "Parent isolé, veuf avec enfants à charge, garde alternée, personne invalide à charge : votre avis peut compter des demi-parts ou des quarts de part que votre situation ne laisse pas deviner. Reportez le chiffre exact, le résultat se recalcule.",
        qCaseT: "Élevez-vous seul vos enfants ?",
        qCaseTAide:
            "C'est la case T de votre déclaration. Votre premier enfant compte alors pour une part entière et non pour une demi-part, ce qui relève votre plafond d'une demi-part de plus et peut à lui seul renverser le verdict.",
        caseTOui: "Oui, seul avec eux",
        caseTOuiDetail: "Personne d'autre ne vit à votre foyer, la case T est cochée",
        caseTNon: "Non",
        caseTNonDetail: "Vous vivez en concubinage, ou l'autre parent est au foyer",
        moins: "Retirer un quart de part",
        plus: "Ajouter un quart de part",

        echeanceTitre: "Date limite d'option",
        echeanceRestant:
            "Il vous reste {n} jours pour opter auprès de l'Urssaf, jusqu'au {date}.",
        echeanceDernierJour:
            "C'est aujourd'hui le dernier jour pour opter auprès de l'Urssaf, le {date}.",
        echeancePassee:
            "La date limite du {date} est passée : pour une activité déjà en cours, l'option pour {annee} n'est plus possible. La prochaine se prend auprès de l'Urssaf avant le 30 septembre {annee}, pour l'année suivante.",
        echeanceCreation:
            "Vous opterez au moment de votre déclaration de création, ou au plus tard le dernier jour du troisième mois qui suit celui de la création : l'option vaut alors dès le démarrage.",

        avertissement:
            "Vous pouvez opter, mais vous n'y gagnez pas forcément : quand un foyer ne paie pas d'impôt sur le revenu, l'option lui en fait payer un à chaque déclaration.",
        versComparateur: "Calculer si vous y gagnez vraiment",

        mecanique: "Comment ce plafond est fixé",
        mecaniqueTexte:
            "Le code des impôts compare le revenu fiscal de référence de l'avant-dernière année à la limite de la deuxième tranche du barème de l'année précédente. Pour {annee}, l'administration regarde donc vos revenus de {rfr}, lus sur l'avis reçu en {avis}, et les compare à {parPart} par part. Chaque année d'option a donc son propre plafond.",
    },
    en: {
        phases: ["Your situation", "Your household", "Your tax notice"],
        retour: "Back",
        recommencer: "Start over",
        verifier: "See if I qualify",
        corriger: "Correct the amount entered",

        qSituation: "Where do you stand?",
        qSituationAide:
            "Your situation sets the year to check and the deadline to opt in. The tax office does not look at this year's income but at the income from two years before the year in question: each card tells you which.",
        actif: "I am already a micro-entrepreneur",
        creeCetteAnnee: "I am starting my business before the end of {annee}",
        creeAnProchain: "I am starting my business in {annee}",
        anneeDetail:
            "Option for {annee}: {rfr} revenu fiscal de référence, on the notice received in {avis}",
        creationDetail: "Option taken when you register, or within the following three months",
        anneeRestant: "{n} days left to opt in, until {date}",
        anneeDernierJour: "Last day to opt in, {date}",
        anneePassee: "Deadline of {date} passed",

        qCouple: "Do you live alone or with a partner?",
        qCoupleAide:
            "The cap is set for one share (part), and your household has one or more: a single person has one, a married or civil-partnered couple filing jointly has two. Its cap therefore goes from {seul} to {couple}. Answer for {rfr}, the year of the notice that counts: if you have married, entered a civil partnership or separated since, the situation on that notice is the one that applies.",
        seul: "Alone",
        seulDetail:
            "Single, divorced, separated or widowed. Widowed with dependent children: enter the shares from your notice afterwards.",
        couple: "With a partner",
        coupleDetail:
            "Married or in a civil partnership, one joint return. Unmarried partners file alone.",

        qEnfants: "How many dependent children?",
        qEnfantsAide:
            "Each child adds half a share, a full one from the third, and the cap follows: with one child, yours rises to {plafond1}. Count the children listed as your dependants on the notice received in {avis}; the exact number of shares can be adjusted on the result.",
        enfants0: "None",
        enfants1: "One",
        enfants2: "Two",
        enfants3: "Three or more",
        enfants3Detail: "Adjustable later if you have more",

        qRfr: "What is your {rfr} revenu fiscal de référence?",
        qRfrAide:
            "For an option in {annee}, only your {rfr} income counts. It appears on the tax notice (avis d'impôt) that arrived in {avis}, on the line labelled Revenu fiscal de référence.",
        partsMaquette:
            "Your notice also shows your number of shares: if it differs from {parts}, you can correct it on the result.",
        reporter: "And yours?",
        champRfr: "{rfr} revenu fiscal de référence",
        champRfrAide:
            "In euros, exactly as shown on the notice, without cents. The exact amount matters: a few euros can tip the result.",

        resultatPour: "Your entitlement for {annee}",
        oui: "You qualify",
        non: "You do not qualify for {annee}",
        plafondParPart: "Cap per share",
        vosParts: "Your shares",
        votrePlafond: "Your cap",
        partSingulier: "share",
        partPluriel: "shares",
        votreRfr: "Your {rfr} revenu fiscal de référence",
        margeLibelle: "Headroom below the cap",
        depassementLibelle: "Amount over the cap",
        ouiMotif:
            "Your {rfr} revenu fiscal de référence, {montant}, does not exceed your household's cap of {plafond}.",
        ouiMarge:
            "That leaves you {marge} of headroom. There is only one other condition: staying under the micro-entreprise turnover ceilings, which you do for as long as you remain a micro-entrepreneur.",
        nonMotif:
            "Your {rfr} revenu fiscal de référence, {montant}, exceeds your household's cap of {plafond} by {depassement}.",
        nonRegle:
            "This income condition comes from article 151-0 of the French tax code: the revenu fiscal de référence of the year before last must not exceed the top of the second tax bracket, {parPart} per share. Your trade and your start date make no difference. Nor does your turnover, as long as you stay within the micro regime.",
        nonSuite:
            "The income year looked at moves forward by one each year: if your income falls, you may be back under the cap next year. For a household taxed like yours, the option would be worth having. Meanwhile, your micro income is taxed on the scale with the rest of your household's income, after the flat allowance for your activity.",

        ajusterLibelle: "Shares on your notice",
        ajusterAide:
            "Single parent, widowed with dependent children, shared custody, a disabled dependant: your notice may carry half or quarter shares that your situation does not reveal. Enter the exact figure and the result recalculates.",
        qCaseT: "Do you raise your children alone?",
        qCaseTAide:
            "This is box T on the French return. Your first child then counts as a full share rather than half a share, which raises your ceiling by another half share and can flip the verdict on its own.",
        caseTOui: "Yes, alone with them",
        caseTOuiDetail: "Nobody else lives in your household, box T is ticked",
        caseTNon: "No",
        caseTNonDetail: "You live with a partner, or the other parent is in the household",
        moins: "Remove a quarter share",
        plus: "Add a quarter share",

        echeanceTitre: "Deadline to opt in",
        echeanceRestant: "You have {n} days left to opt in with URSSAF, until {date}.",
        echeanceDernierJour: "Today, {date}, is the last day to opt in with URSSAF.",
        echeancePassee:
            "The {date} deadline has passed: for a business already trading, the option for {annee} is no longer available. The next one must be filed with URSSAF by 30 September {annee}, for the following year.",
        echeanceCreation:
            "You opt in when you register your business, or at the latest on the last day of the third month after the month you start: the option then applies from day one.",

        avertissement:
            "You can opt in, but you will not necessarily gain from it: for a household that pays no income tax, the option adds a tax charge with every declaration.",
        versComparateur: "Work out whether you actually come out ahead",

        mecanique: "How this cap is set",
        mecaniqueTexte:
            "The tax code compares the revenu fiscal de référence of the year before last against the top of the second bracket of the previous year's scale. For {annee}, the tax office therefore looks at your {rfr} income, shown on the notice received in {avis}, and compares it with {parPart} per share. Each option year therefore has its own cap.",
    },
};

/** Un nombre de parts, avec la virgule française. */
function nombre(v, lang) {
    return v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
}

/**
 * Un montant qui peut porter des centimes : un plafond à 2,5 parts vaut
 * 73 947,50 €, et l'arrondir à 73 948 € ferait afficher un dépassement entre
 * deux nombres égaux. Les montants entiers gardent le format commun.
 */
function eurosExacts(v, lang) {
    return Number.isInteger(v) ? euros(v, lang) : eurosPrecis(v, lang);
}

/**
 * Formateur pour `Montant` : arrondi pendant la montée, exact une fois arrivé.
 * `Montant` multiplie la valeur par une progression qui finit à 1 exactement,
 * si bien que la valeur reçue n'égale la cible qu'au terme de la course.
 */
function exactAuTerme(cible) {
    return (v, lang) => (v === cible ? eurosExacts(v, lang) : euros(v, lang));
}

/**
 * « part » ou « parts ». Le français met le pluriel à partir de 2 (« 1,5 part »),
 * l'anglais dès que ce n'est pas exactement 1 ("1.5 shares").
 */
function unitePart(n, lang, t) {
    const singulier = lang === "fr" ? n < 2 : n === 1;
    return singulier ? t.partSingulier : t.partPluriel;
}

/**
 * Jours restants avant l'échéance : 1 le dernier jour, négatif ou nul ensuite.
 * Le lendemain de la date limite, la lib renvoie -0 (une seconde de retard,
 * arrondie vers zéro) : on le ramène à 0 pour qu'aucun libellé ne le reçoive.
 */
function joursRestants(annee, maintenant) {
    const j = joursAvantEcheance(annee, maintenant);
    return j === 0 ? 0 : j;
}

/**
 * Un montant saisi à la main. Le revenu fiscal de référence est un entier en
 * euros sur l'avis : on ne garde que les chiffres, après avoir retiré
 * d'éventuels centimes (une ou deux décimales en fin de saisie). Un point ou
 * une virgule suivis de trois chiffres sont donc lus comme séparateur de
 * milliers, et « 24,500 » vaut bien 24 500.
 */
function lireMontant(texte) {
    const chiffres = String(texte)
        .trim()
        .replace(/[.,]\d{1,2}$/, "")
        .replace(/\D/g, "");
    return chiffres ? Number.parseInt(chiffres, 10) : NaN;
}

/**
 * Date limite d'option pour une année d'application : le 30 septembre de
 * l'année précédente. La lib ne calcule que les jours restants
 * (`joursAvantEcheance`), la date elle-même est reconstituée ici avec la même
 * constante ; à consolider dans la lib.
 */
function dateLimiteOption(annee) {
    return new Date(Date.UTC(annee - 1, 8, 30));
}

/**
 * L'année d'option que vise chaque situation : l'année prochaine pour une
 * activité en cours ou une création l'an prochain, l'année en cours pour une
 * création d'ici la fin de l'année. Les plafonds, eux, viennent toujours de
 * RFR_PLAFONDS : une année qui n'y figure pas n'est pas proposée.
 */
function anneeVisee(situation, anneeCourante) {
    return situation === "creeCetteAnnee" ? anneeCourante : anneeCourante + 1;
}

/** Un compteur à pas fixe, pour un réglage fin sur un résultat. */
function Compteur({ libelle, valeur, poser, minimum, maximum, pas, formater, moins, plus }) {
    const bouton =
        "w-9 h-9 rounded-lg border border-gray-200/70 dark:border-white/10 text-lg leading-none dark:text-white hover:border-primary/60 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200/70 dark:disabled:hover:border-white/10";
    return (
        <div className="flex justify-between items-center gap-3">
            <span className="text-sm font-semibold dark:text-white">{libelle}</span>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    aria-label={moins}
                    disabled={valeur <= minimum}
                    onClick={() => poser(Math.max(minimum, valeur - pas))}
                    className={bouton}
                >
                    −
                </button>
                <span className="w-12 text-center font-bold tabular-nums dark:text-white">
                    {formater(valeur)}
                </span>
                <button
                    type="button"
                    aria-label={plus}
                    disabled={valeur >= maximum}
                    onClick={() => poser(Math.min(maximum, valeur + pas))}
                    className={bouton}
                >
                    +
                </button>
            </div>
        </div>
    );
}

/**
 * La date limite, dans le ton qui convient à la situation : un compte à
 * rebours pour une activité en cours, la règle de la création pour un
 * créateur, qui n'a aucune raison d'être pressé par le 30 septembre.
 */
function Echeance({ t, lang, annee, jours, creation }) {
    if (!creation && jours === null) return null;
    const date = dateLisible(dateLimiteOption(annee), lang);
    const passee = !creation && jours <= 0;
    const texte = creation
        ? t.echeanceCreation
        : passee
          ? remplir(t.echeancePassee, { date, annee })
          : jours === 1
            ? remplir(t.echeanceDernierJour, { date })
            : remplir(t.echeanceRestant, { n: jours, date });

    return (
        <div
            className={`mt-5 rounded-xl p-4 border ${
                passee
                    ? "border-amber-500/50 bg-amber-500/[0.06]"
                    : "border-primary/40 bg-primary/[0.06]"
            }`}
        >
            <div className="flex items-start gap-2.5">
                <SolarIcon
                    name={
                        passee
                            ? "danger-triangle-linear"
                            : creation
                              ? "calendar-linear"
                              : "clock-circle-linear"
                    }
                    className={`w-5 h-5 shrink-0 mt-0.5 ${
                        passee ? "text-amber-600 dark:text-amber-400" : "text-primary"
                    }`}
                />
                <div className="min-w-0">
                    <div className="text-sm font-semibold dark:text-white">
                        {t.echeanceTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-1 mb-0">
                        {texte}
                    </p>
                </div>
            </div>
        </div>
    );
}

/** Une case du bloc de calcul : une ligne sur mobile, une carte au-dessus. */
function Case({ libelle, valeur, accent = false }) {
    return (
        <div
            className={`rounded-xl px-3 py-2.5 sm:p-3 border flex justify-between items-baseline gap-3 sm:block sm:text-center ${
                accent
                    ? "border-primary/40 bg-primary/[0.06]"
                    : "border-gray-200/70 dark:border-white/10"
            }`}
        >
            <div className="text-[11px] text-gray-500 dark:text-lightBlue/70 sm:mb-1">
                {libelle}
            </div>
            <div className="font-bold tabular-nums dark:text-white shrink-0">{valeur}</div>
        </div>
    );
}

/**
 * Le verdict et son calcul.
 *
 * Composant à part pour que le compteur des montants parte quand le résultat
 * apparaît, pas quand l'outil se charge : un hook appelé dans le composant
 * principal aurait fini sa course bien avant qu'on arrive ici.
 */
function Verdict({
    t,
    lang,
    r,
    montant,
    annee,
    jours,
    creation,
    enCouple,
    enfants,
    partsAjustees,
    poserParts,
    onCorriger,
    reduit,
}) {
    /* Soustraction d'affichage : eligible() ne renvoie pas l'écart au plafond.
       À exposer depuis la lib pour que le composant n'arithmétise plus. */
    const ecart = Math.abs(montant - r.plafond);
    const parPart = euros(r.plafondParPart, lang);
    const plafond = eurosExacts(r.plafond, lang);
    const unite = unitePart(r.parts, lang, t);

    return (
        <>
            <div className="flex items-center gap-2.5 mt-2">
                <SolarIcon
                    name={r.eligible ? "check-circle-linear" : "close-circle-linear"}
                    className={`w-6 h-6 shrink-0 ${
                        r.eligible
                            ? "text-green-600 dark:text-green-400"
                            : "text-amber-600 dark:text-amber-400"
                    }`}
                />
                <div className="font-bold text-lg dark:text-white leading-snug">
                    {r.eligible ? t.oui : remplir(t.non, { annee })}
                </div>
            </div>

            <p className="text-base font-semibold dark:text-white mt-4 mb-0">
                {remplir(r.eligible ? t.ouiMotif : t.nonMotif, {
                    rfr: r.anneeRfr,
                    montant: euros(montant, lang),
                    plafond,
                    depassement: eurosExacts(ecart, lang),
                })}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                <Case libelle={t.plafondParPart} valeur={parPart} />
                <Case libelle={t.vosParts} valeur={`× ${nombre(r.parts, lang)} ${unite}`} />
                <Case
                    libelle={t.votrePlafond}
                    valeur={
                        <>
                            ={" "}
                            <Montant
                                valeur={r.plafond}
                                lang={lang}
                                reduit={reduit}
                                formater={exactAuTerme(r.plafond)}
                            />
                        </>
                    }
                    accent
                />
            </div>

            {/* Le seul réglage du résultat, à un pouce du verdict : ce qu'il
                change doit rester sous les yeux de qui appuie sur + ou −. */}
            <div className="mt-3 rounded-xl p-3 border border-gray-200/70 dark:border-white/10">
                <Compteur
                    libelle={t.ajusterLibelle}
                    valeur={partsAjustees ?? r.parts}
                    poser={poserParts}
                    minimum={1}
                    maximum={10}
                    pas={0.25}
                    formater={(v) => nombre(v, lang)}
                    moins={t.moins}
                    plus={t.plus}
                />
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">
                    {t.ajusterAide}
                </p>
            </div>

            <div className="mt-3 flex justify-between items-baseline gap-4 py-2.5 border-b border-gray-200/70 dark:border-white/10">
                <span className="text-sm dark:text-white">
                    {remplir(t.votreRfr, { rfr: r.anneeRfr })}
                </span>
                <span className="text-sm font-semibold tabular-nums dark:text-white shrink-0">
                    {euros(montant, lang)}
                </span>
            </div>
            <div className="flex justify-between items-baseline gap-4 py-2.5">
                <span className="text-sm dark:text-white">
                    {r.eligible ? t.margeLibelle : t.depassementLibelle}
                </span>
                <span
                    className={`text-sm font-bold tabular-nums shrink-0 ${
                        r.eligible
                            ? "text-green-700 dark:text-green-400"
                            : "text-amber-600 dark:text-amber-400"
                    }`}
                >
                    {r.eligible ? "" : "+ "}
                    <Montant
                        valeur={ecart}
                        lang={lang}
                        reduit={reduit}
                        formater={exactAuTerme(ecart)}
                    />
                </span>
            </div>
            <button
                type="button"
                onClick={onCorriger}
                className="text-xs text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
            >
                {t.corriger}
            </button>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">
                {r.eligible
                    ? remplir(t.ouiMarge, { marge: eurosExacts(ecart, lang) })
                    : remplir(t.nonRegle, { parPart })}
            </p>
            {!r.eligible && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                    {t.nonSuite}
                </p>
            )}

            <Echeance t={t} lang={lang} annee={annee} jours={jours} creation={creation} />

            {r.eligible && (
                <>
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-5 mb-0">
                        {t.avertissement}
                    </p>
                    {/* La couleur vit sur le <button>, hors de portée de la
                        règle globale a:hover qui repeint tout <a> coloré en
                        rose, et le bouton apporte la lèvre 3D qu'un lien
                        fabriqué à la main n'a jamais. */}
                    <div className="mt-4">
                        <Link
                            href={{
                                pathname: "/tools/[slug]",
                                params: {
                                    slug:
                                        lang === "fr"
                                            ? "simulateur-versement-liberatoire"
                                            : "flat-rate-income-tax-calculator",
                                },
                            }}
                        >
                            <Button type="primary" className="press-safe w-full">
                                {t.versComparateur} →
                            </Button>
                        </Link>
                    </div>
                </>
            )}
        </>
    );
}

export default function EligibiliteVersementLiberatoire({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;
    const maintenant = useMemo(() => (aujourdhui ? new Date(aujourdhui) : null), [aujourdhui]);

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [situation, setSituation] = useState(dossier.situation ?? "actif");
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [parentIsole, setParentIsole] = useState(false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [rfr, setRfr] = useState("");
    const [partsAjustees, setPartsAjustees] = useState(null);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "situation",
        () => {
            setSituation("actif");
            setEnCouple(false);
            setEnfants(0);
            setRfr("");
            setPartsAjustees(null);
        },
        PHASES,
        reprendre([
            // Une situation dont l'année n'a pas de barème n'a pas de carte au
            // premier écran : elle ne compte pas comme connue.
            {
                etape: "situation",
                connu:
                    dossier.situation !== undefined &&
                    Boolean(
                        RFR_PLAFONDS[
                            anneeVisee(
                                dossier.situation,
                                maintenant ? maintenant.getUTCFullYear() : ANNEE_OPTION_PAR_DEFAUT - 1,
                            )
                        ],
                    ),
            },
            { etape: "couple", connu: dossier.enCouple !== undefined },
            { etape: "enfants", connu: dossier.enfants !== undefined },
            // La case T ne se déduit pas du dossier, le revenu fiscal de
            // référence non plus : le parcours s'arrête à la première des deux.
            ...(!dossier.enCouple && dossier.enfants >= 1 ? [{ etape: "caseT", connu: false }] : []),
            { etape: "rfr", connu: false },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier({ enCouple, enfants }, etape === "resultat");

    /* Sans date du jour, l'année par défaut de la lib reste la référence. */
    const anneeCourante = maintenant
        ? maintenant.getUTCFullYear()
        : ANNEE_OPTION_PAR_DEFAUT - 1;
    const annee = useMemo(() => {
        const souhaitee = anneeVisee(situation, anneeCourante);
        return RFR_PLAFONDS[souhaitee] ? souhaitee : ANNEE_OPTION_PAR_DEFAUT;
    }, [situation, anneeCourante]);
    const creation = situation !== "actif";
    const bareme = RFR_PLAFONDS[annee];
    const montant = lireMontant(rfr);
    const saisieValide = Number.isFinite(montant) && montant >= 0;

    const r = useMemo(
        () =>
            saisieValide
                ? eligible({
                      revenuFiscalReference: montant,
                      enCouple,
                      enfants,
                      parentIsole,
                      partsFournies: partsAjustees,
                      annee,
                  })
                : null,
        [saisieValide, montant, enCouple, enfants, parentIsole, partsAjustees, annee],
    );

    const jours = maintenant ? joursRestants(annee, maintenant) : null;
    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "situation") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.situation}
                titre={t.qSituation}
                aide={t.qSituationAide}
                choix={SITUATIONS.filter((cle) => RFR_PLAFONDS[anneeVisee(cle, anneeCourante)]).map(
                    (cle) => {
                        const a = anneeVisee(cle, anneeCourante);
                        const b = RFR_PLAFONDS[a];
                        const enCours = cle === "actif";
                        const restants =
                            enCours && maintenant ? joursRestants(a, maintenant) : null;
                        const passee = restants !== null && restants <= 0;
                        const date = dateLisible(dateLimiteOption(a), lang);
                        return {
                            icone: {
                                actif: "user-rounded-linear",
                                creeCetteAnnee: "rocket-2-linear",
                                creeAnProchain: "calendar-linear",
                            }[cle],
                            titre: remplir(t[cle], { annee: a }),
                            detail: (
                                <>
                                    <span className="block">
                                        {remplir(t.anneeDetail, {
                                            annee: a,
                                            rfr: b.anneeRfr,
                                            avis: b.anneeAvis,
                                        })}
                                    </span>
                                    {!enCours && (
                                        <span className="block mt-1 font-semibold text-primary">
                                            {t.creationDetail}
                                        </span>
                                    )}
                                    {restants !== null && (
                                        <span
                                            className={`block mt-1 font-semibold ${
                                                passee ? "" : "text-primary"
                                            }`}
                                        >
                                            {passee
                                                ? remplir(t.anneePassee, { date })
                                                : restants === 1
                                                  ? remplir(t.anneeDernierJour, { date })
                                                  : remplir(t.anneeRestant, { n: restants, date })}
                                        </span>
                                    )}
                                </>
                            ),
                            onClick: () => {
                                setSituation(cle);
                                aller("couple");
                            },
                        };
                    },
                )}
            />
        );
    }

    if (etape === "couple") {
        const plafondSeul = eligible({ revenuFiscalReference: 0, enCouple: false, annee }).plafond;
        const plafondCouple = eligible({ revenuFiscalReference: 0, enCouple: true, annee }).plafond;
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.couple}
                titre={t.qCouple}
                aide={remplir(t.qCoupleAide, {
                    seul: euros(plafondSeul, lang),
                    couple: euros(plafondCouple, lang),
                    rfr: bareme.anneeRfr,
                })}
                choix={[
                    {
                        icone: "user-rounded-linear",
                        titre: t.seul,
                        detail: t.seulDetail,
                        onClick: () => {
                            setEnCouple(false);
                            setPartsAjustees(null);
                            aller("enfants");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.couple,
                        detail: t.coupleDetail,
                        onClick: () => {
                            setEnCouple(true);
                            setPartsAjustees(null);
                            aller("enfants");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "enfants") {
        const plafondUnEnfant = eligible({
            revenuFiscalReference: 0,
            enCouple,
            enfants: 1,
            annee,
        }).plafond;
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.enfants}
                titre={t.qEnfants}
                aide={remplir(t.qEnfantsAide, {
                    plafond1: eurosExacts(plafondUnEnfant, lang),
                    avis: bareme.anneeAvis,
                })}
                choix={[0, 1, 2, 3].map((n) => ({
                    icone: n === 0 ? "close-circle-linear" : "user-plus-linear",
                    titre: t[`enfants${n}`],
                    detail: n === 3 ? t.enfants3Detail : undefined,
                    onClick: () => {
                        setEnfants(n);
                        setPartsAjustees(null);
                        // La case T n'existe que pour une personne seule ayant
                        // au moins un enfant.
                        if (!enCouple && n >= 1) {
                            aller("caseT");
                            return;
                        }
                        setParentIsole(false);
                        aller("rfr");
                    },
                }))}
            />
        );
    }

    if (etape === "caseT") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.caseT}
                titre={t.qCaseT}
                aide={t.qCaseTAide}
                choix={[
                    {
                        icone: "user-hand-up-linear",
                        titre: t.caseTOui,
                        detail: t.caseTOuiDetail,
                        onClick: () => {
                            setParentIsole(true);
                            aller("rfr");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.caseTNon,
                        detail: t.caseTNonDetail,
                        onClick: () => {
                            setParentIsole(false);
                            aller("rfr");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "rfr") {
        const partsDevinees = nombre(parts({ enCouple, enfants, parentIsole }), lang);
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.rfr}
                titre={remplir(t.qRfr, { rfr: bareme.anneeRfr })}
                aide={remplir(t.qRfrAide, {
                    avis: bareme.anneeAvis,
                    rfr: bareme.anneeRfr,
                    annee,
                })}
            >
                {/* La maquette montre le cadre de l'avis où lire le chiffre, jamais
                    le montant du lecteur : le revenu affiché est un
                    exemple, étiqueté « Exemple » par la maquette elle-même, et
                    le bouton reste inactif tant que le sien n'est pas saisi. */}
                <AvisImpotMaquette
                    lang={lang}
                    anneeAvis={bareme.anneeAvis}
                    anneeRevenus={bareme.anneeRfr}
                    montantExemple={MONTANT_EXEMPLE}
                />
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">
                    {remplir(t.partsMaquette, { parts: partsDevinees })}
                </p>

                <form
                    className="mt-6"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (saisieValide) aller("resultat");
                    }}
                >
                    <div className="text-sm font-semibold dark:text-white mb-3">{t.reporter}</div>
                    <label className="block">
                        <span className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">
                            {remplir(t.champRfr, { rfr: bareme.anneeRfr })}
                        </span>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={rfr}
                            onChange={(e) => setRfr(e.target.value)}
                            placeholder={lang === "fr" ? "31 870" : "31,870"}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white tabular-nums focus:outline-hidden focus:border-primary/60"
                        />
                    </label>
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5 mb-0">
                        {t.champRfrAide}
                    </p>
                    {/* La touche Entrée dans le champ passe par le onSubmit du
                        formulaire ; le bouton, lui, avance directement. */}
                    <Avancer
                        pleineLargeur
                        disabled={!saisieValide}
                        onClick={() => saisieValide && aller("resultat")}
                    >
                        {t.verifier}
                    </Avancer>
                </form>
            </Ecran>
        );
    }

    return (
        <Resultat
            onRetour={retour}
            retour={t.retour}
            key={etape}
            titre={remplir(t.resultatPour, { annee })}
            phases={t.phases}
            sens={sens}
            reduit={reduit}
        >
            <Verdict
                t={t}
                lang={lang}
                r={r}
                montant={montant}
                annee={annee}
                jours={jours}
                creation={creation}
                enCouple={enCouple}
                enfants={enfants}
                partsAjustees={partsAjustees}
                poserParts={setPartsAjustees}
                onCorriger={retour}
                reduit={reduit}
            />

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white mb-2">{t.mecanique}</div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0">
                    {remplir(t.mecaniqueTexte, {
                        annee,
                        rfr: bareme.anneeRfr,
                        avis: bareme.anneeAvis,
                        parPart: euros(bareme.plafond, lang),
                    })}
                </p>
            </div>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
